import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, BookOpen, BarChart3, GitCompare, FileText } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();
  
  // Check if on dark page (resources, security, company, webinar, or guides listing)
  const isDarkPage = location.pathname === '/resources' || location.pathname === '/security' || location.pathname === '/company' || location.pathname === '/webinar' || location.pathname === '/guides' || location.pathname.startsWith('/blog');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setResourcesOpen(false);
    setMobileMenuOpen(false);
    setMobileResourcesOpen(false);
  }, [location.pathname]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setResourcesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setResourcesOpen(false), 150);
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Company', path: '/company' },
    { name: 'Security', path: '/security' },
  ];

  const resourceSubLinks = [
    { name: 'Blogs', path: '/blog', icon: FileText },
    { name: 'Guides', path: '/guides', icon: BookOpen },
    { name: 'Use Cases', path: '/use-cases/accounts-payable-automation', icon: BarChart3 },
    { name: 'Comparisons', path: '/comparisons/plouton-vs-traditional-rpa', icon: GitCompare },
  ];

  const isActive = (path) => location.pathname === path;
  const isResourcesActive = location.pathname === '/resources' || location.pathname.startsWith('/guides') || location.pathname.startsWith('/use-cases') || location.pathname.startsWith('/comparisons') || location.pathname.startsWith('/blog');

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isDarkPage 
            ? scrolled 
              ? 'bg-slate-900/90 backdrop-blur-xl border-b border-slate-800' 
              : 'bg-transparent'
            : 'glass border-b border-white/20'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3"
              data-testid="nav-logo-link"
            >
              <img 
                src={LOGO_URL} 
                alt="Plouton AI - Autonomous Finance Workers Logo" 
                className="h-8 sm:h-10 w-auto"
                width={56}
                height={56}
              />
              <span className={`font-heading font-bold text-xl hidden sm:block ${
                isDarkPage ? 'text-white' : 'text-slate-900'
              }`}>
                Plouton AI
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}-link`}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? isDarkPage ? 'text-orange-400' : 'text-blue-600'
                      : isDarkPage 
                        ? 'text-slate-300 hover:text-white' 
                        : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Resources Dropdown */}
              <div 
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                data-testid="nav-resources-dropdown"
              >
                <button
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  className={`text-sm font-medium transition-colors duration-200 flex items-center gap-1 cursor-pointer ${
                    isResourcesActive
                      ? isDarkPage ? 'text-orange-400' : 'text-blue-600'
                      : isDarkPage 
                        ? 'text-slate-300 hover:text-white' 
                        : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="nav-resources-link"
                >
                  Resources
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Panel */}
                {resourcesOpen && (
                  <div 
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                    data-testid="resources-dropdown-panel"
                  >
                    <div className={`w-[280px] rounded-xl border shadow-2xl overflow-hidden ${
                      isDarkPage 
                        ? 'bg-slate-900/95 backdrop-blur-xl border-slate-700/80' 
                        : 'bg-white/95 backdrop-blur-xl border-slate-200'
                    }`}>
                      <div className="py-1.5">
                        {resourceSubLinks.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                              isDarkPage 
                                ? 'hover:bg-slate-800/60' 
                                : 'hover:bg-slate-50'
                            }`}
                            data-testid={`dropdown-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
                              isDarkPage ? 'bg-blue-500/15' : 'bg-blue-50'
                            }`}>
                              <sub.icon className={`w-3.5 h-3.5 ${isDarkPage ? 'text-blue-400' : 'text-blue-600'}`} />
                            </div>
                            <span className={`text-sm font-medium ${isDarkPage ? 'text-slate-200' : 'text-slate-800'}`}>
                              {sub.name}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transition-all duration-300 font-semibold rounded-lg px-6"
                  data-testid="nav-book-demo-button"
                >
                  Book a Demo
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden p-2 ${
                isDarkPage 
                  ? 'text-slate-300 hover:text-white' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div 
              className={`md:hidden py-4 border-t ${
                isDarkPage ? 'border-slate-800 bg-slate-900/95' : 'border-slate-200'
              }`}
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}-link`}
                    className={`text-base font-medium px-3 py-2.5 rounded-lg transition-colors ${
                      isActive(link.path)
                        ? isDarkPage 
                          ? 'text-orange-400 bg-orange-500/10' 
                          : 'text-blue-600 bg-blue-50'
                        : isDarkPage
                          ? 'text-slate-300 hover:text-white hover:bg-slate-800'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                {/* Mobile Resources with Sub-items */}
                <div>
                  <button
                    onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                    data-testid="mobile-nav-resources-toggle"
                    className={`w-full flex items-center justify-between text-base font-medium px-3 py-2.5 rounded-lg transition-colors ${
                      isResourcesActive
                        ? isDarkPage 
                          ? 'text-orange-400 bg-orange-500/10' 
                          : 'text-blue-600 bg-blue-50'
                        : isDarkPage
                          ? 'text-slate-300 hover:text-white hover:bg-slate-800'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    Resources
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {mobileResourcesOpen && (
                    <div className="ml-3 mt-1 space-y-0.5">
                      {resourceSubLinks.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid={`mobile-dropdown-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-2.5 text-sm px-3 py-2 rounded-lg transition-colors ${
                            isDarkPage
                              ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                          }`}
                        >
                          <sub.icon className="w-3.5 h-3.5" />
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <a 
                  href={CAL_LINK} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-3"
                >
                  <Button 
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-lg"
                    data-testid="mobile-book-demo-button"
                  >
                    Book a Demo
                  </Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navigation;
